import { useState } from "react";
import { MapPin } from "lucide-react";
import {
  Caption,
  CardTitle,
  VDialog,
  VDialogContent,
  VField,
  VInput,
  VRadioGroup,
  VTextarea,
} from "@/components/ui";
import type {
  ComplianceChoice,
  ExtensionChoice,
  PolePresence,
  ReservationRequest,
} from "@/v2/shared/model";
import { fullName } from "@/v2/shared/format";
import { useCurrentUser } from "@/v2/auth/useCurrentUser";
import { useUpdateRequest } from "@/v2/data/mutations";
import { reverseGeocode } from "@/v2/data/geocode";
import { MapPicker } from "@/v2/shared/mapPicker";
import { DialogFooter } from "@/v2/aanvragen/detailActions";

type EditErrors = Partial<Record<string, string>>;

// CB-1: aanvraag corrigeren (beheerder). Dezelfde velden als het
// aanvraagformulier, maar gebundeld in één scrollbaar formulier. De locatie
// blijft coördinaatgebaseerd via de kaart; het adres wordt afgeleid.
export function EditRequestDialog({
  open,
  onOpenChange,
  request,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  request: ReservationRequest;
}): React.JSX.Element {
  const user = useCurrentUser();
  const author = fullName(user.firstName, user.lastName);
  const update = useUpdateRequest();

  const [title, setTitle] = useState(request.title);
  const [lastName, setLastName] = useState(request.requesterLastName);
  const [firstName, setFirstName] = useState(request.requesterFirstName);
  const [email, setEmail] = useState(request.requesterEmail);
  const [phone, setPhone] = useState(request.requesterPhone);
  const [service, setService] = useState(request.requesterService);
  const [serviceContact, setServiceContact] = useState(request.requesterServiceContact);

  const [startDate, setStartDate] = useState(request.startDate);
  const [startTime, setStartTime] = useState(request.startTime ?? "");
  const [endDate, setEndDate] = useState(request.endDate);
  const [extension, setExtension] = useState<ExtensionChoice>(request.extension);

  const [address, setAddress] = useState(request.address);
  const [mapPoint, setMapPoint] = useState<{ lat: number; lng: number }>({
    lat: request.locationLat,
    lng: request.locationLng,
  });
  const [addressLoading, setAddressLoading] = useState(false);
  const [locationDescription, setLocationDescription] = useState(request.locationDescription);
  const [polePresence, setPolePresence] = useState<PolePresence>(request.polePresence);
  const [environmentalFactors, setEnvironmentalFactors] = useState(request.environmentalFactors);

  const [event, setEvent] = useState(request.event);
  const [eventInfo, setEventInfo] = useState(request.eventInfo);
  const [parkingSigns, setParkingSigns] = useState(request.parkingSigns);

  const [reason, setReason] = useState(request.reason);
  const [pvNumber, setPvNumber] = useState(request.pvNumber);
  const [specialAuthorization, setSpecialAuthorization] = useState(request.specialAuthorization);
  const [addedValue, setAddedValue] = useState(request.addedValue);
  const [compliance, setCompliance] = useState<ComplianceChoice>(request.compliance);

  const [errors, setErrors] = useState<EditErrors>({});
  const [formError, setFormError] = useState("");

  function clearError(key: string): void {
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  async function handleMapSelect(next: { lat: number; lng: number }): Promise<void> {
    setMapPoint(next);
    setAddressLoading(true);
    const resolved = await reverseGeocode(next.lat, next.lng);
    setAddress(resolved ?? `Locatie op kaart (${next.lat.toFixed(5)}, ${next.lng.toFixed(5)})`);
    setAddressLoading(false);
  }

  function validate(): EditErrors {
    const next: EditErrors = {};
    if (!title.trim()) next.title = "Titel is verplicht.";
    if (!lastName.trim()) next.lastName = "Achternaam is verplicht.";
    if (!firstName.trim()) next.firstName = "Voornaam is verplicht.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) next.email = "Geef een geldig e-mailadres op.";
    if (!phone.trim()) next.phone = "Telefoonnummer is verplicht.";
    if (!service.trim()) next.service = "Dienst is verplicht.";
    if (!serviceContact.trim()) next.serviceContact = "Contactgegevens dienst zijn verplicht.";
    if (!startDate) next.startDate = "Begindatum is verplicht.";
    if (!endDate) next.endDate = "Einddatum is verplicht.";
    if (startDate && endDate && endDate <= startDate) {
      next.endDate = "De einddatum moet na de begindatum liggen.";
    }
    if (!reason.trim()) next.reason = "Reden van aanvraag is verplicht.";
    return next;
  }

  function handleSave(): void {
    const validation = validate();
    setErrors(validation);
    setFormError("");
    if (Object.keys(validation).length > 0) return;
    update.mutate(
      {
        id: request.id,
        author,
        input: {
          title: title.trim(),
          requesterLastName: lastName,
          requesterFirstName: firstName,
          requesterEmail: email,
          requesterPhone: phone,
          requesterService: service,
          requesterServiceContact: serviceContact,
          startDate,
          startTime: startTime || undefined,
          endDate,
          extension,
          address,
          locationLat: mapPoint.lat,
          locationLng: mapPoint.lng,
          locationDescription,
          polePresence,
          environmentalFactors,
          event,
          eventInfo,
          parkingSigns,
          reason,
          pvNumber,
          specialAuthorization,
          addedValue,
          compliance,
        },
      },
      {
        onSuccess: () => onOpenChange(false),
        onError: (e) => setFormError(e.message),
      },
    );
  }

  return (
    <VDialog open={open} onOpenChange={onOpenChange}>
      <VDialogContent
        title="Aanvraag bewerken"
        description="Corrigeer de gegevens van de aanvraag. De toegekende periode van bestaande dossiers verandert hier niet mee."
        size="xl"
      >
        <div className="space-y-6">
          <Section title="Aanvrager">
            <div className="grid gap-4 sm:grid-cols-2">
              <VField label="Achternaam" htmlFor="er-lastname" required error={errors.lastName}>
                <VInput
                  id="er-lastname"
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    clearError("lastName");
                  }}
                />
              </VField>
              <VField label="Voornaam" htmlFor="er-firstname" required error={errors.firstName}>
                <VInput
                  id="er-firstname"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    clearError("firstName");
                  }}
                />
              </VField>
              <VField label="E-mailadres" htmlFor="er-email" required error={errors.email}>
                <VInput
                  id="er-email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    clearError("email");
                  }}
                />
              </VField>
              <VField label="Telefoonnummer" htmlFor="er-phone" required error={errors.phone}>
                <VInput
                  id="er-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    clearError("phone");
                  }}
                />
              </VField>
              <VField label="Dienst" htmlFor="er-service" required error={errors.service}>
                <VInput
                  id="er-service"
                  value={service}
                  onChange={(e) => {
                    setService(e.target.value);
                    clearError("service");
                  }}
                />
              </VField>
              <VField
                label="Contactgegevens dienst"
                htmlFor="er-servicecontact"
                required
                error={errors.serviceContact}
              >
                <VInput
                  id="er-servicecontact"
                  value={serviceContact}
                  onChange={(e) => {
                    setServiceContact(e.target.value);
                    clearError("serviceContact");
                  }}
                />
              </VField>
            </div>
          </Section>

          <Section title="Periode">
            <div className="grid gap-4 sm:grid-cols-3">
              <VField label="Begindatum" htmlFor="er-start" required error={errors.startDate}>
                <VInput
                  id="er-start"
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    clearError("startDate");
                  }}
                />
              </VField>
              <VField label="Beginuur" htmlFor="er-starttime">
                <VInput
                  id="er-starttime"
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                />
              </VField>
              <VField label="Einddatum" htmlFor="er-end" required error={errors.endDate}>
                <VInput
                  id="er-end"
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    clearError("endDate");
                  }}
                />
              </VField>
            </div>
            <VField label="Verlenging mogelijk">
              <VRadioGroup<ExtensionChoice>
                name="er-extension"
                value={extension}
                onChange={setExtension}
                options={[
                  { value: "possible", label: "Ja, verlenging mogelijk (einddatum kan opschuiven)" },
                  { value: "fixed", label: "Nee, vaste einddatum (inzet eindigt strikt op de einddatum)" },
                ]}
              />
            </VField>
          </Section>

          <Section title="Locatie & plaatsbezoek">
            <VField
              label="Locatie op kaart"
              hint="Klik op de kaart of versleep de marker. Het adres wordt automatisch afgeleid."
            >
              <MapPicker point={mapPoint} onSelect={handleMapSelect} />
            </VField>
            <div className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2">
              <MapPin className="size-4 shrink-0 text-primary-600" aria-hidden />
              <Caption tone="default">{addressLoading ? "Adres bepalen…" : address}</Caption>
            </div>
            <VField label="Locatie en kijkrichting" htmlFor="er-locdesc">
              <VTextarea
                id="er-locdesc"
                rows={3}
                value={locationDescription}
                onChange={(e) => setLocationDescription(e.target.value)}
              />
            </VField>
            <VField label="Aanwezigheid paal">
              <VRadioGroup<PolePresence>
                name="er-pole"
                value={polePresence}
                onChange={setPolePresence}
                orientation="horizontal"
                options={[
                  { value: "yes", label: "Ja" },
                  { value: "no", label: "Nee" },
                  { value: "unsure", label: "Weet ik niet zeker" },
                  { value: "notApplicable", label: "Niet van toepassing" },
                ]}
              />
            </VField>
            <VField label="Omgevingsfactoren of bijzonderheden" htmlFor="er-env">
              <VTextarea
                id="er-env"
                rows={2}
                value={environmentalFactors}
                onChange={(e) => setEnvironmentalFactors(e.target.value)}
              />
            </VField>
          </Section>

          <Section title="Evenement & parkeerborden">
            <VField label="Evenement" htmlFor="er-event">
              <VInput id="er-event" value={event} onChange={(e) => setEvent(e.target.value)} />
            </VField>
            <VField label="Info evenement" htmlFor="er-eventinfo">
              <VTextarea
                id="er-eventinfo"
                rows={3}
                value={eventInfo}
                onChange={(e) => setEventInfo(e.target.value)}
              />
            </VField>
            <VField label="Info parkeerborden" htmlFor="er-parking">
              <VInput
                id="er-parking"
                value={parkingSigns}
                onChange={(e) => setParkingSigns(e.target.value)}
              />
            </VField>
          </Section>

          <Section title="Motivatie & conformiteit">
            <VField label="Reden van aanvraag camera" htmlFor="er-reason" required error={errors.reason}>
              <VInput
                id="er-reason"
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  clearError("reason");
                }}
              />
            </VField>
            <div className="grid gap-4 sm:grid-cols-2">
              <VField label="PV-nummer, dossiernummer of dossiernaam" htmlFor="er-pv">
                <VInput id="er-pv" value={pvNumber} onChange={(e) => setPvNumber(e.target.value)} />
              </VField>
              <VField label="Nummer bijzondere machtiging" htmlFor="er-auth">
                <VInput
                  id="er-auth"
                  value={specialAuthorization}
                  onChange={(e) => setSpecialAuthorization(e.target.value)}
                />
              </VField>
            </div>
            <VField label="Meerwaarde inzet camera" htmlFor="er-value">
              <VTextarea
                id="er-value"
                rows={3}
                value={addedValue}
                onChange={(e) => setAddedValue(e.target.value)}
              />
            </VField>
            <VField label="Conformiteit wetgeving">
              <VRadioGroup<ComplianceChoice>
                name="er-compliance"
                value={compliance}
                onChange={setCompliance}
                orientation="horizontal"
                options={[
                  { value: "yes", label: "Ja" },
                  { value: "no", label: "Nee" },
                ]}
              />
            </VField>
          </Section>

          <Section title="Titel">
            <VField
              label="Titel aanvraag"
              htmlFor="er-title"
              required
              hint="Wordt gebruikt voor alle verdere communicatie over de aanvraag."
              error={errors.title}
            >
              <VInput
                id="er-title"
                placeholder="Straat - Startdatum - Dossier/Reden"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  clearError("title");
                }}
              />
            </VField>
          </Section>

          {formError ? <Caption tone="default" className="text-error-600">{formError}</Caption> : null}

          <DialogFooter
            confirmLabel="Opslaan"
            pending={update.isPending}
            onCancel={() => onOpenChange(false)}
            onConfirm={handleSave}
          />
        </div>
      </VDialogContent>
    </VDialog>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="space-y-4">
      <CardTitle className="text-sm">{title}</CardTitle>
      {children}
    </div>
  );
}
